// src/pages/auth/ForgotPassword.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, CheckCircle, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { authService } from '../../services/auth';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import { getAssetUrl } from '../../utils/url';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [logoError, setLogoError] = useState(false);

  const { settings, fetchPublicSettings } = useSystemSettingsStore();

  // Fetch public settings on mount (same as Login)
  useEffect(() => {
    fetchPublicSettings();
  }, [fetchPublicSettings]);

  // Reset logo error when logo_url changes
  useEffect(() => {
    setLogoError(false);
  }, [settings.logo_url]);

  // Dynamic values from settings
  const appName = settings.app_name || 'SIM-EVAL';
  const appDescription = settings.app_description || 'Sistem Informasi Manajemen Evaluasi';
  const institutionName = settings.institution_name || 'Universitas Buana Perjuangan Karawang';
  const footerText = settings.footer_text || '© 2026 SIM-EVAL UBP Karawang. All rights reserved.';

  const logoUrl = getAssetUrl(settings.logo_url);
  const showLogo = logoUrl && !logoError;

  // Generate brand initials from app name
  const brandInitials = appName
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();

  // Update document title and favicon
  useEffect(() => {
    if (appName) {
      document.title = `Lupa Password - ${appName}`;
    }
    if (settings.favicon_url) {
      const faviconUrl = getAssetUrl(settings.favicon_url);
      if (faviconUrl) {
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = faviconUrl;
      }
    }
  }, [appName, settings.favicon_url]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Email wajib diisi');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Format email tidak valid');
      return;
    }

    setLoading(true);
    try {
      // ✅ FIXED: Uncomment actual API call
      await authService.forgotPassword(email);
      setSent(true);
      toast.success('Email reset password telah dikirim!');
    } catch (err: any) {
      console.error('Forgot password error:', err);
      if (err.response?.status === 404 || err.response?.status === 422) {
        // Check if it's validation error for email not found
        const emailError = err.response.data?.errors?.email?.[0];
        if (emailError) {
          setError(emailError);
        } else {
          setError('Email tidak terdaftar dalam sistem');
        }
      } else if (err.response?.status === 429) {
        setError('Terlalu banyak permintaan. Silakan tunggu beberapa menit.');
      } else {
        setError(err.response?.data?.message || 'Terjadi kesalahan. Silakan coba lagi.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      // ✅ FIXED: Uncomment actual API call
      await authService.forgotPassword(email);
      toast.success('Email telah dikirim ulang!');
    } catch (err: any) {
      console.error('Resend error:', err);
      if (err.response?.status === 429) {
        toast.error('Terlalu banyak permintaan. Silakan tunggu beberapa menit.');
      } else {
        toast.error('Gagal mengirim ulang email');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-dark via-teal-main to-teal-light flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo - Consistent with Login */}
        <div className="text-center mb-8">
          <div
            className={`inline-flex items-center justify-center w-16 h-16 mb-4 animate-bounce overflow-hidden rounded-3xl ${showLogo ? 'bg-transparent' : 'bg-white shadow-2xl'
              }`}
          >
            {showLogo ? (
              <img
                src={logoUrl!}
                alt="Logo"
                className="w-full h-full object-contain"
                onError={() => setLogoError(true)}
              />
            ) : (
              <span className="text-3xl font-black bg-gradient-to-r from-teal-dark to-teal-main bg-clip-text text-transparent">
                {brandInitials}
              </span>
            )}
          </div>
          <h1 className="text-4xl font-black text-white tracking-tight drop-shadow-lg">{appName}</h1>
          <p className="text-sm text-white/80 mt-2 font-medium">{appDescription}</p>
          <p className="text-xs text-white/70 mt-1">{institutionName}</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          {!sent ? (
            <>
              {/* Form State */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-teal-main/10 rounded-xl mb-3">
                  <Mail className="w-6 h-6 text-teal-main" />
                </div>
                <h2 className="text-xl font-bold text-teal-dark">Lupa Password?</h2>
                <p className="text-sm text-neutral-text-secondary mt-1">
                  Masukkan email Anda dan kami akan mengirimkan link untuk reset password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-text-secondary" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError('');
                      }}
                      placeholder="nama@ubpkarawang.ac.id"
                      className={`
                        w-full pl-10 pr-4 py-3 rounded-xl border text-sm
                        focus:outline-none focus:ring-2 focus:ring-teal-light
                        ${error ? 'border-danger' : 'border-neutral-border'}
                      `}
                      autoFocus
                    />
                  </div>
                  {error && <p className="text-xs text-danger">{error}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`
                    w-full py-3 rounded-xl font-bold text-sm text-white
                    bg-gradient-to-r from-teal-dark to-teal-main
                    hover:from-teal-dark hover:to-teal-light
                    shadow-lg shadow-teal-main/40 hover:shadow-xl hover:shadow-teal-main/50
                    focus:outline-none focus:ring-2 focus:ring-teal-light focus:ring-offset-2
                    transition-all duration-300
                    ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}
                  `}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Mengirim...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Send className="w-4 h-4" />
                      Kirim Link Reset
                    </span>
                  )}
                </button>
              </form>
            </>
          ) : (
            <>
              {/* Success State */}
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-4">
                  <CheckCircle className="w-8 h-8 text-success" />
                </div>
                <h2 className="text-xl font-bold text-teal-dark">Email Terkirim!</h2>
                <p className="text-sm text-neutral-text-secondary mt-2 leading-relaxed">
                  Kami telah mengirimkan link reset password ke{' '}
                  <span className="font-semibold text-teal-main">{email}</span>.
                  Silakan cek inbox atau folder spam Anda.
                </p>

                <div className="mt-6 p-4 bg-neutral-bg rounded-xl">
                  <p className="text-xs text-neutral-text-secondary">
                    Tidak menerima email?
                  </p>
                  <button
                    onClick={handleResend}
                    disabled={loading}
                    className="text-sm font-semibold text-teal-main hover:underline mt-1 disabled:opacity-50"
                  >
                    {loading ? 'Mengirim ulang...' : 'Kirim ulang email'}
                  </button>
                </div>

                <div className="mt-4 p-3 bg-warning/5 border border-warning/20 rounded-xl">
                  <p className="text-xs text-warning">
                    ⏰ Link reset password berlaku selama 60 menit.
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm font-medium text-teal-main hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Kembali ke halaman login
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-white/50 mt-6 font-medium">
          {footerText}
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;